import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import SeoHead from "@/components/feature/SeoHead";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MangaCard from "@/components/feature/MangaCard";
import { categories, mangaWorks } from "@/mocks/manga";

const PAGE_SIZE = 12;

export default function CategoryPage() {
  const { categoryId = "all" } = useParams();
  const [searchParams] = useSearchParams();
  const q = searchParams.get("q") ?? "";
  const navigate = useNavigate();

  const currentCategory = categories.find((c) => c.id === categoryId);
  const [sort, setSort] = useState<"hot" | "latest">("hot");
  const [page, setPage] = useState(1);

  const baseList = mangaWorks.filter((work) =>
    q
      ? work.title.includes(q) ||
        work.author.includes(q) ||
        work.tags.some((tag) => tag.includes(q))
      : categoryId === "all" || work.category === categoryId,
  );

  const list = [...baseList].sort((a, b) =>
    sort === "latest" ? b.updateTime.localeCompare(a.updateTime) : a.hot - b.hot,
  );

  const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const visibleList = list.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [categoryId, q, sort]);

  const switchCategory = (id: string) => {
    if (id === "all") {
      navigate("/category/all");
    } else {
      navigate(`/category/${id}`);
    }
  };

  const switchPage = (target: number) => {
    if (target < 1 || target > totalPages) return;
    setPage(target);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const pageNumbers: number[] = [];
  const start = Math.max(1, page - 2);
  const end = Math.min(totalPages, page + 2);
  for (let i = start; i <= end; i += 1) pageNumbers.push(i);

  const pageTitle = q
    ? `搜索“${q}”漫画结果-43423漫画网`
    : `${currentCategory?.name ?? "漫画"}漫画分类-免费在线阅读-43423漫画网`;
  const pageDesc = q
    ? `在43423漫画网搜索“${q}”相关漫画，共找到${list.length}部作品，全部支持免费在线阅读。`
    : `${currentCategory?.name ?? "漫画"}分类漫画全集免费在线阅读，海量${currentCategory?.name ?? "漫画"}题材作品高清连载，每日更新，随时开卷。`;

  const jsonLd = [
    {
      "@type": "CollectionPage",
      url: `https://workoutpro0.github.io/category/${categoryId}`,
      name: pageTitle,
      inLanguage: "zh-CN",
      isPartOf: { "@id": "https://workoutpro0.github.io/#website" },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://workoutpro0.github.io/" },
        { "@type": "ListItem", position: 2, name: "漫画大全", item: "https://workoutpro0.github.io/category/all" },
        { "@type": "ListItem", position: 3, name: `${currentCategory?.name ?? "全部"}漫画` },
      ],
    },
  ];

  return (
    <>
      <SeoHead
        title={pageTitle}
        description={pageDesc}
        path={`/category/${categoryId}`}
        type="website"
        jsonLd={jsonLd}
      />

      <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-6 md:py-8">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "漫画大全", to: "/category/all" },
            { label: q ? `搜索：${q}` : `${currentCategory?.name ?? "全部"}漫画` },
          ]}
        />

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
              {q ? `搜索“${q}”` : `${currentCategory?.name ?? "全部"}漫画`}
            </h1>
            <p className="mt-1.5 text-xs text-foreground-600 md:text-sm">
              共找到 <strong className="font-bold text-primary-600">{list.length}</strong> 部作品，全部支持免费高清在线阅读
            </p>
          </div>
          <label className="flex items-center gap-2 text-xs text-foreground-600">
            排序：
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as "hot" | "latest")}
              className="rounded-md border border-background-200 bg-background-100 px-3 py-2 text-xs font-bold text-foreground-900 focus:border-primary-400 focus:outline-none"
            >
              <option value="hot">人气最高</option>
              <option value="latest">最新更新</option>
            </select>
          </label>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          {categories.map((cat) => {
            const active = !q && cat.id === categoryId;
            const count =
              cat.id === "all"
                ? mangaWorks.length
                : mangaWorks.filter((w) => w.category === cat.id).length;
            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => switchCategory(cat.id)}
                className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-xs font-bold transition-colors ${
                  active
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-600"
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}漫画
                <span className={`rounded-full px-1.5 text-[10px] ${active ? "bg-background-50/20" : "bg-background-200"}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {visibleList.length > 0 ? (
          <>
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 md:gap-4 xl:grid-cols-6">
              {visibleList.map((work) => (
                <MangaCard key={work.id} work={work} />
              ))}
            </div>

            {totalPages > 1 && (
              <nav className="mt-8 flex items-center justify-center gap-2" aria-label="分页导航">
                <button
                  type="button"
                  onClick={() => switchPage(page - 1)}
                  disabled={page === 1}
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-background-200 bg-background-100 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
                  aria-label="上一页"
                >
                  <i className="ri-arrow-left-s-line text-lg"></i>
                </button>
                {pageNumbers.map((num) => (
                  <button
                    key={num}
                    type="button"
                    onClick={() => switchPage(num)}
                    className={`h-9 w-9 rounded-md text-xs font-bold transition-colors ${
                      num === page
                        ? "bg-primary-500 text-background-50"
                        : "border border-background-200 bg-background-100 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                    }`}
                  >
                    {num}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => switchPage(page + 1)}
                  disabled={page === totalPages}
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-background-200 bg-background-100 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
                  aria-label="下一页"
                >
                  <i className="ri-arrow-right-s-line text-lg"></i>
                </button>
              </nav>
            )}
          </>
        ) : (
          <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-2xl text-foreground-500">
              <i className="ri-book-2-line"></i>
            </span>
            <h2 className="mt-4 text-sm font-bold text-foreground-950">
              {q ? `没有找到与“${q}”相关的漫画` : "该分类下暂时没有作品"}
            </h2>
            <p className="mt-1.5 text-xs text-foreground-600">换个关键词试试，或浏览全站漫画大全</p>
            <button
              type="button"
              onClick={() => navigate("/category/all")}
              className="mt-5 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-xs font-bold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-apps-2-line"></i>
              查看全部漫画
            </button>
          </div>
        )}
      </div>
    </>
  );
}